import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Leaf, ArrowLeft } from "lucide-react";

export default function Login() {
  const { login, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success("Willkommen zurück!");
      navigate("/premium");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Anmeldung fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/premium";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#F3EFE6] flex items-center justify-center px-4 fb-grain">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#4A5D52] hover:text-[#1E3F20] mb-6">
          <ArrowLeft className="w-4 h-4" /> Zurück zur Startseite
        </Link>
        <div className="bg-white rounded-3xl ring-1 ring-[#E2DDD3] shadow-xl p-8">
          <div className="flex items-center gap-2 mb-6">
            <img src="/foodbone-logo.png" alt="FoodBone" className="h-11 w-11 object-contain" />
            <span className="font-display text-xl font-bold text-[#1E3F20]">Food&amp;Bone</span>
          </div>
          <h1 className="font-display text-2xl font-bold text-[#1C2D24] mb-1">Anmelden</h1>
          <p className="text-[#4A5D52] text-sm mb-6">Melde dich an, um deinen Premium-Zugang zu nutzen.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-[#1C2D24]">E-Mail</Label>
              <Input id="email" type="email" data-testid="login-email-input" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 bg-[#F9F8F3] border-[#E2DDD3]" placeholder="ihre@email.de" />
            </div>
            <div>
              <Label htmlFor="password" className="text-[#1C2D24]">Passwort</Label>
              <Input id="password" type="password" data-testid="login-password-input" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 bg-[#F9F8F3] border-[#E2DDD3]" placeholder="••••••••" />
            </div>
            <Button type="submit" data-testid="login-submit-button" disabled={loading} className="w-full bg-[#1E3F20] hover:bg-[#163017] text-white rounded-full h-11">
              {loading ? "Anmelden..." : "Anmelden"}
            </Button>
          </form>
          <div className="my-5 flex items-center gap-3">
            <span className="h-px flex-1 bg-[#E2DDD3]" />
            <span className="text-xs text-[#769777]">oder</span>
            <span className="h-px flex-1 bg-[#E2DDD3]" />
          </div>
          <Button type="button" data-testid="google-login-button" onClick={googleLogin} variant="outline" className="w-full rounded-full h-11 border-[#E2DDD3] gap-2 hover:bg-[#F3EFE6]">
            <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5c-.3 1.5-1.1 2.8-2.4 3.7v3h3.9c2.3-2.1 3.5-5.2 3.5-8.9z"/><path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3c-1.1.7-2.5 1.2-4.1 1.2-3.1 0-5.8-2.1-6.8-5H1.2v3.1C3.2 21.3 7.3 24 12 24z"/><path fill="#FBBC05" d="M5.2 14.3c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3V6.6H1.2C.4 8.2 0 10 0 12s.4 3.8 1.2 5.4l4-3.1z"/><path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4C18 1.2 15.2 0 12 0 7.3 0 3.2 2.7 1.2 6.6l4 3.1c1-2.9 3.7-4.9 6.8-4.9z"/></svg>
            Mit Google anmelden
          </Button>
          <p className="text-sm text-[#4A5D52] text-center mt-6">
            Noch kein Konto?{" "}
            <Link to="/register" data-testid="go-register-link" className="text-[#2D5A27] font-semibold hover:underline">Jetzt registrieren</Link>
          </p>
        </div>
        <p className="flex items-center justify-center gap-1.5 text-xs text-[#769777] mt-6">
          <Leaf className="w-3.5 h-3.5" /> Sicher &amp; verschlüsselt
        </p>
      </div>
    </div>
  );
}
